import styled, { css, keyframes } from 'styled-components';

import { resetButton } from 'styles/common';

// ── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const orbPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 1; }
`;

const ringRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ── Container ────────────────────────────────────────────────────────────────

export const Wrapper = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

// ── Expanded Panel ───────────────────────────────────────────────────────────

export const Panel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(10, 8, 30, 0.92);
  border: 1px solid rgba(0, 200, 255, 0.25);
  border-radius: 16px;
  backdrop-filter: blur(16px);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.5),
    0 0 1px rgba(0, 200, 255, 0.3);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

// ── Avatar ───────────────────────────────────────────────────────────────────

export const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 1.5px solid rgba(0, 200, 255, 0.4);
  background: #000;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

// ── Voice Orb (voice activity visualization) ─────────────────────────────────

export const OrbWrap = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const OrbRing = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 200, 255, 0.3);

  ${({ $active }) =>
    $active &&
    css`
      border-color: rgba(0, 200, 255, 0.6);
      animation: ${ringRotate} 3s linear infinite;
      border-style: dashed;
      border-width: 1.5px;
    `}
`;

export const OrbCore = styled.div<{ $active: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $active }) =>
    $active
      ? 'radial-gradient(circle, rgba(0, 220, 255, 0.9) 0%, rgba(0, 140, 255, 0.6) 100%)'
      : 'radial-gradient(circle, rgba(80, 80, 120, 0.6) 0%, rgba(40, 40, 80, 0.4) 100%)'};
  box-shadow: ${({ $active }) =>
    $active ? '0 0 12px rgba(0, 200, 255, 0.5), 0 0 4px rgba(0, 200, 255, 0.3)' : 'none'};
  transition: all 0.3s ease;

  ${({ $active }) =>
    $active &&
    css`
      animation: ${orbPulse} 1.5s ease-in-out infinite;
    `}
`;

// ── Equalizer bars ───────────────────────────────────────────────────────────

export const EqBars = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 24px;
  flex-shrink: 0;
`;

export const EqBar = styled.div<{ $active: boolean; $i: number }>`
  width: 2.5px;
  height: 100%;
  border-radius: 1px;
  background: ${({ $active }) => ($active ? 'rgba(0, 200, 255, 0.8)' : 'rgba(100, 100, 140, 0.4)')};
  transform-origin: center;
  transition:
    background 0.3s,
    transform 0.06s linear;

  ${({ $active }) =>
    !$active &&
    css`
      transform: scaleY(0.15);
    `}
`;

// ── Control buttons ──────────────────────────────────────────────────────────

export const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const Btn = styled.button<{ $variant?: 'danger' }>`
  ${resetButton}
  width: 30px;
  height: 30px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: rgba(200, 210, 240, 0.7);
  transition: all 0.2s;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: currentColor;
    }
  }

  &:disabled {
    opacity: 0.35;
    pointer-events: none;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.95);
    background: rgba(255, 255, 255, 0.08);
  }

  ${({ $variant }) =>
    $variant === 'danger' &&
    css`
      color: rgba(255, 100, 100, 0.6);

      &:hover {
        color: rgba(255, 100, 100, 0.95);
        background: rgba(255, 100, 100, 0.1);
      }
    `}
`;

// ── Collapsed (mini) state ───────────────────────────────────────────────────

export const Mini = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(10, 8, 30, 0.92);
  border: 1px solid rgba(0, 200, 255, 0.25);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.5),
    0 0 1px rgba(0, 200, 255, 0.3);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

export const ExpandBtn = styled.button`
  ${resetButton}
  width: 26px;
  height: 26px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: rgba(200, 210, 240, 0.6);
  transition: all 0.2s;

  svg {
    width: 14px;
    height: 14px;

    path {
      fill: currentColor;
    }
  }

  &:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.08);
  }
`;
